import SocketIOClient from "socket.io-client";
import nonceFast from "nonce-fast";
import Sha from 'jssha';

interface RequestInitQs extends RequestInit {
  qs?: any;
  json?: any;
}

class ApiError extends Error {
  response: Response;

  constructor(response: Response, body: any) {
    const message = body
      ? body.message || body
      : response.status >= 500
        ? "Unknown Server Error"
        : "Bad Request";
    super(message);
    this.name = response.status >= 500 ? "ServerError" : "ClientError";
    this.response = response;
  }
}

class ApiClient {
  onError?: (e: ApiError) => void;
  private socket?: SocketIOClient.Socket;
  private generateNonce = nonceFast(10);

  sha256Hmac(key: string, message: string): string {
    const sha = new Sha("SHA-256", "TEXT")
    sha.setHMACKey(key, "TEXT")
    sha.update(message)
    return sha.getHMAC("HEX")
  }

  sign(secret: string, nonce: string, ts: string): string {
    return this.sha256Hmac(secret, [nonce, ts].join(' '));
  }

  private qs2string(qs: any) {
    const terms: Array<string> = [];
    const isEmpty = (v: any) => v === null || v === undefined;
    const encode = (key: string, value: any) =>
      `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    for (let [key, value] of Object.entries(qs)) {
      if (isEmpty(value)) {
        continue;
      }
      const values: Array<any> = Array.isArray(value) ? value : [value];
      for (const v of values) {
        if (isEmpty(v)) {
          continue;
        }
        terms.push(encode(key, v));
      }
    }
    return terms.join("&");
  }

  get(url: string, opts?: RequestInitQs) {
    return this.request("GET", url, opts);
  }

  put(url: string, opts?: RequestInitQs) {
    return this.request("PUT", url, opts);
  }

  post(url: string, opts?: RequestInitQs) {
    return this.request("POST", url, opts);
  }

  delete(url: string, opts?: RequestInitQs) {
    return this.request("DELETE", url, opts);
  }

  generateAuth(): any {
    const secret = localStorage.getItem('secret');
    if (secret) {
      const nonce = this.generateNonce();
      const ts = new Date().toISOString();
      const sign = this.sign(secret, nonce, ts);
      return { nonce, ts, sign };
    }
    return {}
  }

  getUrl(url: string) {
    return `api${url}`
  }

  async request(method: string, url: string, opts: RequestInitQs = {}) {
    opts.method = method;
    opts.headers = opts.headers || {};
    opts.headers = { ...opts.headers, ...this.generateAuth() };
    const headers = opts.headers as any;
    if (opts.qs) {
      const qs = this.qs2string(opts.qs);
      if (qs) {
        url += `?${qs}`;
      }
    }
    if (opts.json) {
      opts.body = JSON.stringify(opts.json);
      headers["content-type"] = "application/json; charset=utf-8";
    }

    const res = await fetch(this.getUrl(url), opts);
    const contentType = res.headers.get("content-type");
    let body: any;
    if (contentType && ~contentType.indexOf("application/json")) {
      body = await res.json();
    } else {
      body = await res.text();
    }
    if (res.status >= 400) {
      const e = new ApiError(res, body);
      if (this.onError) {
        this.onError(e);
      }
      throw e;
    }
    return body;
  }

  emit(event: string, data?: any) {
    const { socket } = this;
    if (!socket) {
      return;
    }
    socket.emit(event, data);
  }

  connectSocket(handlers: { [key: string]: CallableFunction }) {

    this.socket = SocketIOClient({ path: `${window.location.pathname}api/socket.io` });
    for (const [event, handler] of Object.entries(handlers)) {
      this.socket.on(event, handler);
    }
  }

  async authSocket() {
    const { socket } = this;
    if (!socket) {
      throw new Error('Please call connectSocket first');
    }
    const auth = await this.generateAuth();
    if (!auth.sign) {
      throw new Error('Invalid auth body');
    }
    socket.emit('auth', auth);
  }
}

export default new ApiClient();
